import type { Store } from './store';

/**
 * The operations the UI gates on, named by the same string the API's permission
 * table uses (`services.Operation` in lambdas/api/services/permissions.go).
 *
 * Only operations the interface actually hides or disables need a name here.
 * This list is a convenience for call sites, not a policy: which of them a
 * given user may perform is decided by the server and arrives on
 * `store.permissions`.
 */
export const StoreOperation = {
  CancelOrder: 'delete-order',
  ReadDashboard: 'read-dashboard',
  SendOrder: 'send-order',
  UpdateOrder: 'update-order',
} as const;

export type StoreOperation = (typeof StoreOperation)[keyof typeof StoreOperation];

/**
 * Human-readable requirement for an operation, for messages explaining why a
 * control is unavailable. Demo is deliberately left out: it is an internal
 * role, and naming it to a store owner would only confuse.
 *
 * Every role may update an order, so that requirement only ever reads to an
 * account with no relation to the store at all — today, a developer looking
 * at a customer's store.
 */
export const OperationRequirement: Record<StoreOperation, string> = {
  [StoreOperation.CancelOrder]: 'Cancelling an order requires the Manager or Owner role.',
  [StoreOperation.ReadDashboard]: 'The dashboard requires the Owner role.',
  [StoreOperation.SendOrder]: 'Sending an order requires the Manager or Owner role.',
  [StoreOperation.UpdateOrder]: 'Editing an order requires a role on this store.',
};

/**
 * Whether this user may perform `operation` on `store`.
 *
 * The answer is computed by the API and shipped on the store, rather than
 * derived here from the user's role. Two reasons, both learned the hard way:
 * the role table alone cannot express the API's admin bypasses, so an exempt
 * user with no relation row had every gated control hidden; and a second copy
 * of the policy drifts, silently, in whichever direction nobody tests.
 *
 * An absent list means the store predates this field — an older API during a
 * deploy, or a payload rehydrated from IndexedDB without passing through Yup.
 * That resolves permissive: the control is offered, and the API refuses it if
 * it must. Hiding controls on a store we simply have no answer for would take
 * the failure the user can act on and replace it with one they cannot.
 */
export function can(store: Store, operation: StoreOperation): boolean {
  if (!store.permissions) {
    return true;
  }

  return store.permissions.includes(operation);
}

/**
 * Why `operation` is unavailable on `store`, or null when it is allowed —
 * the shape a control's `disabledReason` wants.
 */
export function unavailableReason(store: Store, operation: StoreOperation): string | null {
  return can(store, operation) ? null : OperationRequirement[operation];
}
